import React, { useEffect, useRef, useState } from "react";

export interface ZikrCardProps extends React.HTMLAttributes<HTMLDivElement> {
    data?: {
        preZikr: string;
        zikrMushakal: string;
        zikr: string;
        postZikr: string;
        ajr: string;
        count: number;
        sources: Array<string>;
        quraan: boolean;
        content: string;
        source: string;
    };
    active: string;
    index?: number;
    key?: string;
}

const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
function toArabic(n: number): string {
    return n
        .toString()
        .split("")
        .map((d) => arabicNumerals[parseInt(d)] ?? d)
        .join("");
}

const ZikrCard: React.FC<ZikrCardProps> = ({
    data = { count: 0 } as any,
    active,
    index = 0,
    ...rest
}) => {
    const [count, setCount] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [pulse, setPulse] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    const isVibrationSupported = typeof navigator !== "undefined" &&
        navigator.vibrate !== undefined;

    function vibrateOnTap() {
        if (isVibrationSupported) {
            try {
                navigator.vibrate(18);
            } catch {
                /* noop */
            }
        }
    }

    const today = new Date().toJSON().slice(0, 10);
    const storageKey = today + active + index;

    /* hydrate from localStorage */
    useEffect(() => {
        const savedState = localStorage.getItem(storageKey);
        if (savedState) {
            const parsed = JSON.parse(savedState);
            setCount(parsed);
            if (parsed >= (data?.count ?? 0)) setCompleted(true);
        } else {
            setCount(0);
            setCompleted(false);
        }
    }, [storageKey, data?.count]);

    /* persist count */
    useEffect(() => {
        if (count !== 0 && localStorage.localStorageNotice) {
            localStorage.setItem(storageKey, count + "");
        }
    }, [count, storageKey]);

    useEffect(() => {
        if (data?.count && count >= data.count && !completed) {
            setCompleted(true);
        }
    }, [count, data?.count, completed]);

    const increment = () => {
        if (completed) return;
        vibrateOnTap();
        setCount((prev) => Math.min(prev + 1, data?.count ?? prev + 1));
        setPulse(true);
        window.setTimeout(() => setPulse(false), 280);
    };

    const reset = () => {
        setCount(0);
        if (localStorage.localStorageNotice) {
            localStorage.setItem(storageKey, "0");
        }
        setCompleted(false);
    };

    const complete = () => {
        setCount(data?.count ?? 0);
        setCompleted(true);
    };

    const total = data?.count ?? 0;
    const progress = total > 0 ? Math.min(count / total, 1) : 0;
    const arabicText = data?.zikrMushakal ? data.zikrMushakal : data?.zikr;

    const cardBaseClasses =
        "relative gleam rounded-3xl p-4 transition-all duration-700 ease-dreamy will-change-transform";

    const surfaceStyle: React.CSSProperties = completed
        ? {
              backgroundImage:
                  "linear-gradient(135deg, hsla(var(--hue-a),70%,82%,0.85), hsla(var(--hue-c),80%,86%,0.9))",
              boxShadow:
                  "0 24px 60px -16px hsla(var(--hue-b),70%,40%,0.4), inset 0 1px 0 rgba(255,255,255,0.7)",
          }
        : count > 0
        ? {
              backgroundImage:
                  "linear-gradient(135deg, rgba(255,255,255,0.65), rgba(255,255,255,0.4))",
              boxShadow:
                  "0 16px 44px -16px hsla(var(--hue-b),60%,40%,0.3), inset 0 1px 0 rgba(255,255,255,0.7)",
          }
        : {
              backgroundImage:
                  "linear-gradient(135deg, rgba(255,255,255,0.5), rgba(255,255,255,0.28))",
              boxShadow:
                  "0 12px 36px -16px hsla(var(--hue-b),50%,40%,0.22), inset 0 1px 0 rgba(255,255,255,0.6)",
          };

    if (!data) return <div>data missing</div>;

    return (
        <div
            {...rest}
            ref={cardRef}
            dir="rtl"
            className={`${cardBaseClasses} ${
                pulse ? "animate-soft-pulse" : ""
            }`}
            style={{
                backdropFilter: "blur(18px) saturate(180%)",
                WebkitBackdropFilter: "blur(18px) saturate(180%)",
                border: "1px solid rgba(255,255,255,0.6)",
                ...surfaceStyle,
            }}
        >
            {/* progress halo */}
            <div
                className="absolute inset-x-4 -top-px h-[2px] rounded-full overflow-hidden opacity-80"
                aria-hidden
            >
                <div
                    className="h-full rounded-full transition-all duration-500 ease-dreamy"
                    style={{
                        width: `${progress * 100}%`,
                        background:
                            "linear-gradient(90deg, hsl(var(--hue-a),80%,55%), hsl(var(--hue-c),80%,60%))",
                        boxShadow:
                            "0 0 8px hsla(var(--hue-b),80%,55%,0.6)",
                    }}
                />
            </div>

            {/* control row */}
            <div className="flex flex-row-reverse items-center justify-between text-xs text-slate-500 mb-2">
                <button
                    className="tap-pop px-3 py-1 rounded-full hover:bg-white/40 transition-colors duration-300 font-medium"
                    onClick={reset}
                >
                    إعادة
                </button>
                <button
                    className="tap-pop px-3 py-1 rounded-full hover:bg-white/40 transition-colors duration-300 font-medium"
                    onClick={complete}
                >
                    إنهاء
                </button>
                <span className="font-semibold text-slate-600">
                    {toArabic(index + 1)}
                </span>
                <span className="font-medium text-slate-600 tabular-nums">
                    <span
                        className={`count-pop inline-block ${
                            completed ? "text-emerald-700" : ""
                        }`}
                        style={{
                            transform: pulse ? "scale(1.18)" : "scale(1)",
                        }}
                    >
                        {toArabic(count)}
                    </span>
                    <span className="opacity-60 mx-1">/</span>
                    <span>{toArabic(total)}</span>
                </span>
            </div>

            {/* tappable text region */}
            <div
                onClick={increment}
                className="cursor-pointer select-none text-center relative"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        increment();
                    }
                }}
            >
                {data.preZikr && (
                    <p
                        className="arabic text-sm text-slate-500 mb-1"
                        style={{
                            color: "hsl(var(--hue-c), 35%, 38%)",
                        }}
                    >
                        {data.preZikr}
                    </p>
                )}

                <p
                    className={`${
                        data.quraan ? "quran" : "arabic"
                    } text-xl md:text-2xl font-semibold leading-loose text-slate-800`}
                >
                    {arabicText}
                </p>

                {data.content && (
                    <p className="arabic text-base md:text-lg leading-loose text-slate-700 mt-1">
                        {data.content}
                    </p>
                )}

                {(data.postZikr || data.source) && (
                    <p
                        className="arabic text-xs mt-2 italic"
                        style={{
                            color: "hsl(var(--hue-a), 35%, 40%)",
                        }}
                    >
                        {data.postZikr}
                        {data.source}
                    </p>
                )}

                {data.ajr && (
                    <p
                        className="arabic text-xs mt-2 leading-relaxed"
                        style={{
                            color: "hsl(var(--hue-c), 50%, 38%)",
                        }}
                    >
                        {data.ajr}
                    </p>
                )}
            </div>

            {completed && (
                <div
                    aria-hidden
                    className="absolute inset-0 rounded-3xl pointer-events-none completed-shimmer opacity-25 mix-blend-overlay"
                />
            )}
        </div>
    );
};

export default ZikrCard;
